import { useState, useEffect } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { useAppSelector } from '../../store/hooks';
import { apiGetActivePacks, apiGetMyCardAccount, apiOpenPack } from '../../api/userCardApi';
import type { ActivePack, PackOpenResult } from '../../api/userCardApi';
import { apiGetCard } from '../../api/cardApi';
import { apiGetExpansion } from '../../api/expansionApi';
import type { ExpansionCardItem } from '../../types/models/expansion';
import PackRevealModal from './PackRevealModal';
import CardDetailModal from './CardDetailModal';
import type { Card } from '../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string) =>
  path.startsWith('http') ? path : storageUrl + path;

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
  flex: 1;
`;

const PageSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 28px;
`;

const CreditsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
`;

const CreditsLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreditsValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: gold;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 28px;
`;

const PackCard = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
    transform: translateY(-2px);
  }
`;

const PackImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const PackImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PackImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 12px;
`;

const PackBody = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const PackName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const PackMeta = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
`;

const PackNotes = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
  margin: 4px 0 0;
  line-height: 1.4;
`;

const PackHint = styled.p`
  font-size: 11px;
  color: rgba(255, 215, 0, 0.5);
  margin: 4px 0 0;
`;

const PackCost = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: gold;
  margin: 4px 0 0;
`;

const BuyBtn = styled.button<{ $disabled?: boolean }>`
  margin: 12px 16px 16px;
  padding: 10px;
  background-color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.05)' : 'rgba(255,215,0,0.15)')};
  border: 1px solid ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.2)' : 'gold')};
  border-radius: 6px;
  color: ${({ $disabled }) => ($disabled ? 'rgba(255,215,0,0.35)' : 'gold')};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background-color: rgba(255, 215, 0, 0.25);
  }
`;

const ErrorMsg = styled.p`
  font-size: 12px;
  color: #e05555;
  margin: 0 16px 12px;
  text-align: center;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 48px;
  font-size: 15px;
`;

// ─── Expansion Preview Modal ───────────────────────────────────────────────────

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.82);
  z-index: 900;
  overflow-y: auto;
`;

const PreviewInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  box-sizing: border-box;
`;

const PreviewDialog = styled.div`
  width: 100%;
  max-width: 900px;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PreviewTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: gold;
  margin: 0 0 4px;
`;

const PreviewSubtitle = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const PreviewCloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  padding: 0;
  &:hover { color: #fff; }
`;

const PreviewBody = styled.div`
  padding: 20px 24px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 10px;
`;

const PreviewCardWrapper = styled.div`
  aspect-ratio: 2 / 3;
  border-radius: 6px;
  overflow: hidden;
  background-color: #2a2a2a;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.15s, border-color 0.15s;
  &:hover {
    transform: scale(1.04);
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const PreviewCardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewCardPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(255,255,255,0.15);
`;

const PreviewEmpty = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.3);
  font-size: 14px;
  padding: 32px 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PackShopPage: React.FC = () => {
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [packs, setPacks] = useState<ActivePack[]>([]);
  const [credits, setCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openingId, setOpeningId] = useState<number | null>(null);
  const [packErrors, setPackErrors] = useState<Record<number, string>>({});
  const [revealResult, setRevealResult] = useState<PackOpenResult | null>(null);

  // Preview espansione
  const [previewPack, setPreviewPack] = useState<ActivePack | null>(null);
  const [previewCards, setPreviewCards] = useState<ExpansionCardItem[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expansionCache, setExpansionCache] = useState<Record<number, ExpansionCardItem[]>>({});

  // Dettaglio carta da preview
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [detailCache, setDetailCache] = useState<Record<number, Card>>({});

  useEffect(() => {
    apiGetActivePacks()
      .then((data) => setPacks(Array.isArray(data) ? data : (data as any).data ?? []))
      .catch(() => setPacks([]))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyCardAccount()
      .then((data) => setCredits(data.credits))
      .catch(() => {});
  }, [isAuthenticated]);

  const handlePackClick = async (pack: ActivePack) => {
    setPreviewPack(pack);
    const expId = pack.expansion?.id;
    if (!expId) { setPreviewCards([]); return; }
    if (expansionCache[expId]) { setPreviewCards(expansionCache[expId]); return; }
    setPreviewLoading(true);
    setPreviewCards(null);
    try {
      const exp = await apiGetExpansion(expId);
      const cards = exp.cards ?? [];
      setExpansionCache((prev) => ({ ...prev, [expId]: cards }));
      setPreviewCards(cards);
    } catch {
      setPreviewCards([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBuy = async (e: React.MouseEvent, pack: ActivePack) => {
    e.stopPropagation();
    setOpeningId(pack.id);
    setPackErrors((prev) => ({ ...prev, [pack.id]: '' }));
    try {
      const result = await apiOpenPack(pack.id);
      if (!Array.isArray(result.cards)) throw new Error('invalid_response');
      setCredits(result.credits_remaining);
      setRevealResult(result);
    } catch (err: any) {
      const msg = err?.message?.includes('insufficient_credits')
        ? 'Crediti insufficienti'
        : 'Errore durante l\'acquisto';
      setPackErrors((prev) => ({ ...prev, [pack.id]: msg }));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <>
    <CommonWrapper>
      <PageInner>
        <PageHeader>
          <PageTitle>Acquista Pacchetti</PageTitle>
          {isAuthenticated && (
            <CreditsBar>
              <CreditsLabel>I tuoi crediti:</CreditsLabel>
              <CreditsValue>{credits !== null ? credits : '—'}</CreditsValue>
            </CreditsBar>
          )}
        </PageHeader>
        <PageSubtitle>Clicca su un pacchetto per vedere un'anteprima delle carte che puoi ottenere.</PageSubtitle>

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {!isLoading && packs.length === 0 && (
          <StatusText>Nessun pacchetto disponibile.</StatusText>
        )}

        <Grid>
          {packs.map((pack) => {
            const src = pack.expansion?.expansion_path
              ? toImgSrc(pack.expansion.expansion_path)
              : null;
            return (
              <PackCard key={pack.id} onClick={() => handlePackClick(pack)}>
                <PackImgWrapper>
                  {src
                    ? <PackImg src={src} alt={pack.expansion.name} />
                    : <PackImgPlaceholder>Nessuna immagine</PackImgPlaceholder>
                  }
                </PackImgWrapper>
                <PackBody>
                  <PackName>{pack.name}</PackName>
                  <PackMeta>{pack.cards_per_pack} carte per pacchetto</PackMeta>
                  <PackMeta>{pack.expansion.name}</PackMeta>
                  {pack.expansion.notes && <PackNotes>{pack.expansion.notes}</PackNotes>}
                  <PackCost>{pack.cost} crediti</PackCost>
                  <PackHint>Clicca per vedere le carte disponibili</PackHint>
                </PackBody>
                {packErrors[pack.id] && <ErrorMsg>{packErrors[pack.id]}</ErrorMsg>}
                <BuyBtn
                  $disabled={!isAuthenticated || openingId === pack.id}
                  disabled={!isAuthenticated || openingId === pack.id}
                  onClick={(e) => isAuthenticated && handleBuy(e, pack)}
                >
                  {!isAuthenticated
                    ? 'Accedi per acquistare'
                    : openingId === pack.id
                      ? 'Apertura...'
                      : `Acquista — ${pack.cost} crediti`}
                </BuyBtn>
              </PackCard>
            );
          })}
        </Grid>
      </PageInner>
    </CommonWrapper>

    {/* Modale anteprima carte espansione */}
    {previewPack && (
      <PreviewOverlay onClick={(e) => e.target === e.currentTarget && setPreviewPack(null)}>
        <PreviewInner>
          <PreviewDialog onClick={(e) => e.stopPropagation()}>
            <PreviewHeader>
              <div>
                <PreviewTitle>Carte disponibili</PreviewTitle>
                <PreviewSubtitle>{previewPack.expansion?.name} · {previewCards?.length ?? '…'} carte nel pool</PreviewSubtitle>
              </div>
              <PreviewCloseBtn onClick={() => setPreviewPack(null)}>✕</PreviewCloseBtn>
            </PreviewHeader>
            <PreviewBody>
              {previewLoading && <PreviewEmpty>Caricamento...</PreviewEmpty>}
              {!previewLoading && previewCards?.length === 0 && (
                <PreviewEmpty>Nessuna carta trovata per questa espansione.</PreviewEmpty>
              )}
              {!previewLoading && previewCards && previewCards.length > 0 && (
                <PreviewGrid>
                  {previewCards.map((card) => {
                    const imgSrc = card.fronte_path ? toImgSrc(card.fronte_path) : null;
                    return (
                      <PreviewCardWrapper key={card.id} onClick={async () => {
                          if (detailLoading === card.id) return;
                          if (detailCache[card.id]) { setDetailCard(detailCache[card.id]); return; }
                          setDetailLoading(card.id);
                          try {
                            const full = await apiGetCard(card.id);
                            setDetailCache((prev) => ({ ...prev, [card.id]: full }));
                            setDetailCard(full);
                          } catch { } finally { setDetailLoading(null); }
                        }}
                        style={{ cursor: detailLoading === card.id ? 'wait' : 'pointer' }}
                      >
                        {imgSrc
                          ? <PreviewCardImg src={imgSrc} alt={`Carta #${card.id}`} />
                          : <PreviewCardPlaceholder>#{card.id}</PreviewCardPlaceholder>
                        }
                      </PreviewCardWrapper>
                    );
                  })}
                </PreviewGrid>
              )}
            </PreviewBody>
          </PreviewDialog>
        </PreviewInner>
      </PreviewOverlay>
    )}

    {/* Dettaglio carta da anteprima */}
    {detailCard && (
      <CardDetailModal
        card={detailCard}
        ownedQuantity={0}
        isAuthenticated={isAuthenticated}
        onClose={() => setDetailCard(null)}
      />
    )}

    {revealResult && (
      <PackRevealModal
        cards={revealResult.cards}
        alreadyOwned={revealResult.already_owned}
        creditsRemaining={revealResult.credits_remaining}
        onClose={() => setRevealResult(null)}
      />
    )}
    </>
  );
};

export default PackShopPage;
